const $ = (id) => document.getElementById(id);

export function formatTime(sec) {
  if (sec == null || !isFinite(sec)) return '--:--.--';
  const s = Math.max(0, sec);
  const m = Math.floor(s / 60);
  const rem = s - m * 60;
  return `${String(m).padStart(2, '0')}:${rem.toFixed(2).padStart(5, '0')}`;
}

/**
 * Thin wrapper over the HUD markup. Everything is change-detected because
 * writing to textContent every frame on sixty elements is a measurable cost
 * on a phone, and it also causes the layout to jitter.
 */
export class HUD {
  constructor() {
    this.root = $('hud');
    this.name = $('hud-name');
    this.obj = $('hud-obj');
    this.timer = $('hud-timer');
    this.shards = $('hud-shards');
    this.speedFill = $('speed-fill');
    this.speedText = $('speed-text');
    this.speedo = $('speedo');
    this.altText = $('alt-text');
    this.altMark = $('alt-mark');
    this.alt = $('alt');
    this.cross = $('cross');
    this.pipA = $('pip-a');
    this.pipB = $('pip-b');
    this.toast = $('toast');
    this.combo = $('combo');
    this.taunt = $('taunt');
    this.rival = $('hud-rival');
    this.hurt = $('hurt');
    this.flash = $('flash');
    this.fps = $('fps');

    this._cache = {};
    this._toastTimer = 0;
    this._comboTimer = 0;
    this._tauntTimer = 0;
    this._hurt = 0;
    this._flash = 0;
  }

  /**
   * @param {null | {lead:number, distance:number}} state lead is metres of
   * course progress the player is ahead by; negative means the rival leads.
   */
  setRival(state) {
    if (!this.rival) return;
    this.rival.classList.toggle('hidden', !state);
    if (!state) return;
    // `lead` is how far the PLAYER is ahead, but the readout is labelled
    // RIVAL, so the words invert: player ahead means the rival is behind.
    const playerAhead = state.lead >= 0;
    this.rival.classList.toggle('behind', !playerAhead);
    const label = `${Math.abs(Math.round(state.lead))}M ${playerAhead ? 'BEHIND' : 'AHEAD'}`;
    if (this._cache.rival !== label) {
      this._cache.rival = label;
      this.rival.innerHTML = `RIVAL <b>${label}</b>`;
    }
  }

  showTaunt(text, seconds = 4.5) {
    if (!this.taunt || !text) return;
    this.taunt.textContent = text;
    this.taunt.classList.add('show');
    this._tauntTimer = seconds;
  }

  show(on) { this.root?.classList.toggle('hidden', !on); }

  _set(key, el, value) {
    if (!el || this._cache[key] === value) return;
    this._cache[key] = value;
    el.textContent = value;
  }

  setLevel(name, objective) {
    this._set('name', this.name, name);
    this._set('obj', this.obj, objective);
  }

  setObjective(text) { this._set('obj', this.obj, text); }

  setTimer(sec, { countdown = false, danger = false } = {}) {
    this._set('timer', this.timer, formatTime(sec));
    if (this.timer) {
      const want = danger ? '#ff3355' : (countdown ? '#ffb020' : '');
      if (this._cache.timerColor !== want) {
        this._cache.timerColor = want;
        this.timer.style.color = want;
        this.timer.style.textShadow = want ? `0 0 16px ${want}` : '';
      }
    }
  }

  setShards(got, total) {
    if (!this.shards) return;
    const on = total > 0;
    this.shards.classList.toggle('hidden', !on);
    if (on) this._set('shards', this.shards, `SHARDS ${got}/${total}`);
  }

  setSpeed(speed, max = 70) {
    const pct = Math.min(100, (speed / max) * 100);
    if (this.speedFill) this.speedFill.style.width = `${pct.toFixed(1)}%`;
    // Rebuilding innerHTML every frame forces a parse; only write on change.
    const label = speed.toFixed(1);
    if (this.speedText && this._cache.speed !== label) {
      this._cache.speed = label;
      this.speedText.innerHTML = `VEL <b>${label}</b> M/S`;
    }
    this.speedo?.classList.toggle('hot', speed > max * 0.62);
  }

  setAltitude(y, spawnY, range = 140) {
    const alt = String(Math.round(y));
    if (this.altText && this._cache.alt !== alt) {
      this._cache.alt = alt;
      this.altText.innerHTML = `ALT <b>${alt}</b>M`;
    }
    const rel = Math.max(-1, Math.min(1, (y - spawnY) / range));
    if (this.altMark) this.altMark.style.top = `${(0.5 - rel * 0.5) * 100}%`;
    this.alt?.classList.toggle('deep', y < spawnY - range * 0.25);
  }

  setPortals(aOn, bOn) {
    this.pipA?.classList.toggle('on', aOn);
    this.pipB?.classList.toggle('on', bOn);
  }

  setCrosshair(state) {
    if (!this.cross) return;
    if (this._cache.cross === state) return;
    this._cache.cross = state;
    this.cross.classList.remove('valid', 'invalid');
    if (state) this.cross.classList.add(state);
  }

  /** One standing instruction (first-flight hints); null clears it. */
  setHint(text) {
    this.hint ??= document.getElementById('hint');
    if (!this.hint) return;
    if (this._hintText === text) return;
    this._hintText = text;
    if (text) this.hint.textContent = text;
    this.hint.classList.toggle('show', !!text);
  }

  showToast(text, warn = false, seconds = 2.2) {
    if (!this.toast) return;
    this.toast.textContent = text;
    this.toast.classList.toggle('warn', warn);
    this.toast.classList.add('show');
    this._toastTimer = seconds;
  }

  showCombo(text) {
    if (!this.combo) return;
    this.combo.textContent = text;
    this.combo.style.opacity = '1';
    this.combo.style.transform = 'translate(-50%,-50%) scale(1.12)';
    this._comboTimer = 1.1;
  }

  setFps(value, visible) {
    if (!this.fps) return;
    this.fps.classList.toggle('hidden', !visible);
    if (visible) this._set('fps', this.fps, `${Math.round(value)} FPS`);
  }

  hit(amount = 1) { this._hurt = Math.max(this._hurt, amount); }
  bang(amount = 0.5) { this._flash = Math.max(this._flash, amount); }

  update(dt) {
    if (this._toastTimer > 0) {
      this._toastTimer -= dt;
      if (this._toastTimer <= 0) this.toast?.classList.remove('show');
    }
    if (this._comboTimer > 0) {
      this._comboTimer -= dt;
      const k = Math.max(0, this._comboTimer / 1.1);
      if (this.combo) {
        this.combo.style.opacity = String(k);
        this.combo.style.transform = `translate(-50%,-50%) scale(${1 + (1 - k) * 0.35})`;
      }
    }
    if (this._tauntTimer > 0) {
      this._tauntTimer -= dt;
      if (this._tauntTimer <= 0) this.taunt?.classList.remove('show');
    }
    if (this._hurt > 0) {
      this._hurt = Math.max(0, this._hurt - dt * 1.5);
      if (this.hurt) this.hurt.style.opacity = String(this._hurt * 0.9);
    }
    if (this._flash > 0) {
      this._flash = Math.max(0, this._flash - dt * 3.2);
      if (this.flash) this.flash.style.opacity = String(this._flash);
    }
  }
}
